import React from 'react';
import { Box, useTheme } from '@mui/material';
import { KeyValuePairProps } from './KeyValuePair.props';

/**
 * KeyValuePair - Simple component for displaying label-value pairs
 *
 * @example
 * ```tsx
 * <KeyValuePair label="Loan Amount" value="$396,900" />
 * <KeyValuePair
 *   label={<Typography variant="body2">Interest Rate</Typography>}
 *   value={<Typography variant="h6">6.50%</Typography>}
 * />
 * <KeyValuePair
 *   label="Credit Score"
 *   value="695"
 *   labelSx={{ height: '32px', maxWidth: '150px' }}
 *   valueSx={{ height: '48px', maxHeight: '60px' }}
 * />
 * ```
 */
export const KeyValuePair: React.FC<KeyValuePairProps> = ({
  label,
  value,
  direction = 'horizontal',
  spacing = 'sm',
  align = 'normal',
  labelSx,
  valueSx,
  fullWidth,
  sx,
  ...boxProps
}) => {
  const theme = useTheme();
  // Convert spacing to MUI theme spacing multiplier
  const getSpacingMultiplier = (
    spacingValue: KeyValuePairProps['spacing']
  ): number | undefined => {
    if (spacingValue === undefined) return undefined;
    if (typeof spacingValue === 'number') return spacingValue;

    switch (spacingValue) {
      case 'none':
        return 0;
      case 'xs':
        return 0.5;
      case 'sm':
        return 1;
      case 'md':
        return 2;
      case 'lg':
        return 3;
      case 'xl':
        return 4;
      default:
        return undefined;
    }
  };

  const spacingMultiplier = getSpacingMultiplier(spacing);
  const isHorizontal = direction === 'horizontal';

  return (
    <Box
      {...boxProps}
      sx={{
        display: 'flex',
        flexDirection: isHorizontal ? 'row' : 'column',
        alignItems: align,
        ...(spacingMultiplier !== undefined && {
          gap: theme.unit(spacingMultiplier),
        }),
        ...(fullWidth && { width: '100%' }),
        ...sx,
      }}
    >
      <Box
        sx={{
          ...(isHorizontal && { flexShrink: 0 }),
          ...theme.typography.M3.body.medium,
          color: theme.palette.M3.sys.light['on-surface-variant'],
          '& *': {
            ...theme.typography.M3.body.medium,
          },
          ...labelSx,
        }}
      >
        {label}
      </Box>
      <Box
        sx={{
          ...(isHorizontal && { flex: 1 }),
          ...theme.typography.M3.body.mediumProminent,
          color: theme.palette.M3.sys.light['on-surface'],
          '& *': {
            ...theme.typography.M3.body.mediumProminent,
          },
          ...valueSx,
        }}
      >
        {value}
      </Box>
    </Box>
  );
};

export default KeyValuePair;
